// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — fiches financières (DÉMONSTRATION) :
//   • créance : solde calculé, mouvements en ajout seul (avoir, abandon, régularisation proposés puis validés par une
//     2e personne ; correction = contrepassation proposée puis validée), échéancier versionné, actes de gestion
//     (suspension, report, rééchelonnement) proposés puis validés — lien « À traiter » vers la créance ;
//   • paiement : préparé → signé → contresigné → exécuté (pièce obligatoire) ; annulation motivée avant exécution ;
//     bénéficiaire et signataires nommés (DM-WEB-002 n° 4) ; chèque désigné ;
//     après exécution, l'annulation passe par la contrepassation de l'écriture (onglet Comptabilité).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, FileSignature, Gavel, PenLine, Play, Plus, Undo2, Wrench, XCircle } from 'lucide-react';
import { Badge, Bouton, BandeauInfo, Squelette } from '../../design/composants';
import { toArray, libelle, aujourdhuiDakar } from './api';
import { useEspaces, droitsDans } from './espaces';
import { Champ, Etat, AccesRefuseDs, ErreurServeur, ListePieces } from './commun';
import {
  useSocial, francs, dateLongue, lireMontant, ETATS_CREANCE, ETATS_PAIEMENT, MODES_PAIEMENT, OBJETS_PAIEMENT, NATURES_MOUVEMENT,
} from './apiSocial';
import {
  useLectureSociale, PageFiche, Infos, TitreSection, HistoriqueActes, FenetreActe, MarqueDemo, Montant, motifValide, ChampMotif, ChoixRadio,
  usePieceDepot, ChampPiece, BandeauComptaDemo,
} from './communSocial';
import { Echeanciers, Mouvements, BadgeSimulation } from './EcheanciersCommun';
import { FenetreProposerActe, FenetreDeciderActe, resumeActe } from './ActesCreance';
import { SuiviDossierGestion } from '../../components/suivi/SuiviDemande'; // prochain acteur (lecture)

const ligneActe = (c) => ({ salarie: c.salarie, numero: c.reference, creance: { id: c.creance_id, solde: c.solde, etat: c.etat } });
const ORIGINES = { PRET: 'Prêt social', INSCRIPTION: 'Campagne', COTISATION: 'Cotisation', PARTS: 'Parts sociales' };
const PIECES_PAIEMENT = ['RECU', 'AVIS_VIREMENT', 'COPIE_CHEQUE', 'DECISION', 'FACTURE', 'RELEVE', 'JUSTIFICATIF'];

function FenetreMouvement({ creance, onFermer, onFait }) {
  const api = useSocial();
  const [nature, setNature] = useState('AVOIR');
  const [sens, setSens] = useState('-1');
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState(aujourdhuiDakar());
  const [motif, setMotif] = useState('');
  const m = lireMontant(montant);
  return (
    <FenetreActe titre="Proposer un mouvement" Icone={Plus} libelleBouton="Proposer" pret={!!(m && motifValide(motif))} onFermer={onFermer}
      aide="Le mouvement est proposé ici et ne compte qu'après sa validation par une autre personne. Le bénéficiaire ne propose ni ne valide jamais."
      onConfirmer={async (cle) => {
        await api.proposerMouvement(creance.creance_id, { nature, ...(nature === 'REGULARISATION' ? { sens: Number(sens) } : {}), montant: m, date_operation: date, motif: motif.trim() }, cle);
        onFait('Mouvement proposé : il attend la validation d\'une autre personne.');
      }}>
      <ChoixRadio nom="nature" options={{ AVOIR: 'Avoir (diminue le restant dû)', ABANDON: 'Abandon de créance', REGULARISATION: 'Régularisation' }} valeur={nature} onChange={setNature} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        {nature === 'REGULARISATION' && (
          <Champ libelle="Sens" requis>
            <select className="rh-champ-saisie" value={sens} onChange={(e) => setSens(e.target.value)}><option value="-1">Diminue le restant dû</option><option value="1">Augmente le restant dû</option></select>
          </Champ>
        )}
        <Champ libelle="Montant (F)" requis aide={`Restant dû : ${francs(creance.solde)}`}><input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} /></Champ>
        <Champ libelle="Date d'opération" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
      </div>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

function FenetreDeciderMouvement({ mouvement, valider, onFermer, onFait }) {
  const api = useSocial();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre={`${valider ? 'Valider' : 'Rejeter'} : ${libelle(NATURES_MOUVEMENT, mouvement.nature).toLowerCase()} de ${francs(mouvement.montant)}`}
      Icone={valider ? CheckCircle2 : XCircle} libelleBouton={valider ? 'Valider' : 'Rejeter'} danger={!valider} pret={motifValide(motif)} onFermer={onFermer}
      aide="La personne qui a proposé le mouvement ne le valide pas (double regard)."
      onConfirmer={async (cle) => { await api.deciderMouvement(mouvement.id, { decision: valider ? 'VALIDER' : 'REJETER', motif: motif.trim() }, cle); onFait(valider ? 'Mouvement validé : le solde est recalculé.' : 'Mouvement rejeté.'); }}>
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>Motif de la proposition : « {mouvement.motif} »</p>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

function FenetreContrepasser({ mouvement, onFermer, onFait }) {
  const api = useSocial();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre="Proposer une contrepassation" Icone={Undo2} libelleBouton="Proposer la contrepassation" danger pret={motifValide(motif)} onFermer={onFermer}
      aide={`Un mouvement ne se modifie ni ne se supprime : la correction est un mouvement inverse exact (${francs(mouvement.montant)}), validé par une autre personne.`}
      onConfirmer={async (cle) => { await api.contrepasserMouvement(mouvement.id, motif.trim(), cle); onFait('Contrepassation proposée.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

/** Actes de gestion d'une créance (hors prêt) : en attente (si le serveur les liste), proposer, décider. */
function ActesGestionCreance({ c, droits, vivante, onActe }) {
  if (c.origine_type === 'PRET') {
    return <p style={{ margin: 0, fontSize: 14 }}>Les actes de gestion d'un prêt (suspension, report, rééchelonnement) se proposent et se valident depuis <Link className="rh-lien" to={`/dispositifs/${c.organisation_id}/prets/${c.origine_id}`}>la fiche du prêt</Link>.</p>;
  }
  const listes = Array.isArray(c.actes_en_attente);
  const enAttente = toArray(c.actes_en_attente);
  const peutValider = droits.a('ds_creance_mouvement_valider');
  return (
    <>
      {listes && !enAttente.length && <p className="rh-cellule-secondaire" style={{ margin: '0 0 10px' }}>Aucun acte en attente de validation.</p>}
      {enAttente.length > 0 && (
        <ul style={{ margin: '0 0 10px', padding: 0, listStyle: 'none' }}>
          {enAttente.map((a) => (
            <li key={a.decision_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--sg-bord)', fontSize: 14 }}>
              <span><Badge ton="alerte" point>À valider</Badge> {resumeActe(a)}{a.propose_par ? ` — proposé par ${a.propose_par}` : ''}{a.proposition?.motif ? ` · « ${a.proposition.motif} »` : ''}</span>
              {peutValider && <Bouton petit variante="primaire" Icone={Gavel} onClick={() => onActe({ type: 'decider-acte', decisionId: a.decision_id })}>Valider ou rejeter</Bouton>}
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {vivante && droits.a('ds_creance_mouvement_proposer') && <Bouton petit Icone={Wrench} onClick={() => onActe({ type: 'proposer-acte' })}>Proposer un acte</Bouton>}
        {!listes && peutValider && <Bouton petit Icone={Gavel} onClick={() => onActe({ type: 'decider-acte', decisionId: '' })}>Décider d'un acte proposé</Bouton>}
      </div>
    </>
  );
}

export function CreancePage() {
  const { org, id } = useParams();
  const api = useSocial();
  const { espaces } = useEspaces();
  const fiche = useLectureSociale(() => api.creance(id), [api, id]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const c = fiche.donnees;
  const droits = droitsDans(espaces, c?.organisation_id || org);
  const vivante = c && ['ACTIVE', 'SUSPENDUE', 'LITIGE', 'DIRECTE'].includes(c.etat);
  const fait = (m) => { setFenetre(null); setInfo(m); fiche.recharger(); };
  const actionsLigne = (m) => {
    if (m.statut === 'A_VALIDER') {
      const cle = m.nature === 'CONTREPASSATION' ? 'ds_contrepassation_valider' : 'ds_creance_mouvement_valider';
      if (!droits.a(cle)) return null;
      return (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Bouton petit variante="primaire" onClick={() => setFenetre({ type: 'decider', m, valider: true })}>Valider</Bouton>
          <Bouton petit onClick={() => setFenetre({ type: 'decider', m, valider: false })}>Rejeter</Bouton>
        </div>
      );
    }
    if (m.statut === 'VALIDE' && m.nature !== 'CONTREPASSATION' && m.nature !== 'CONSTITUTION' && droits.a('ds_contrepassation_proposer')) {
      return <Bouton petit variante="discret" Icone={Undo2} onClick={() => setFenetre({ type: 'contrepasser', m })}>Contrepasser</Bouton>;
    }
    return null;
  };
  return (
    <PageFiche section="tresorerie" libelleSection="Trésorerie" titre={c ? `Créance ${c.reference}` : 'Créance'}
      actions={vivante && droits.a('ds_creance_mouvement_proposer') ? <Bouton variante="primaire" Icone={Plus} onClick={() => setFenetre({ type: 'mouvement' })}>Proposer un mouvement</Bouton> : null}>
      <BandeauComptaDemo />
      {fiche.erreur && (fiche.erreur.statut === 403 || fiche.erreur.statut === 404
        ? <section className="rh-carte"><AccesRefuseDs erreur={fiche.erreur} /></section>
        : <ErreurServeur erreur={fiche.erreur} />)}
      {!c && !fiche.erreur && <Squelette hauteur={160} />}
      {c && (
        <>
          {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
          {c.simulation && <BandeauInfo ton="info" titre="Créance de simulation">Issue d'une campagne de simulation : jamais transmise à la Paie, aucun paiement réel.</BandeauInfo>}
          <section className="rh-carte">
            <TitreSection>Créance</TitreSection>
            <Infos lignes={[
              ['Salarié', `${c.salarie} (${c.matricule})`], ['Origine', <>{libelle(ORIGINES, c.origine_type)}{c.origine_type === 'PRET' && c.origine_id ? <> · <Link className="rh-lien" to={`/dispositifs/${c.organisation_id}/prets/${c.origine_id}`}>voir le prêt</Link></> : null}</>],
              ['État', <Etat table={ETATS_CREANCE} code={c.etat} />], ['Montant initial', <Montant n={c.montant_initial} />],
              ['Total débité', <Montant n={c.total_debit} />], ['Total crédité', <Montant n={c.total_credit} />], ['Restant dû', <Montant n={c.solde} fort />],
              ['Remboursement', c.mode_remboursement === 'RETENUE' ? 'Retenue sur salaire' : 'Versements directs'],
              ['Retenue sur salaire', c.retenue_possible ? 'Possible (fondement enregistré)' : 'Impossible : fondement juridique absent'],
              ['Créée le', dateLongue(c.cree_le)], ['Démonstration', c.donnees_demo ? <MarqueDemo actif /> : null], ['Simulation', c.simulation ? <BadgeSimulation actif /> : null],
            ]} />
          </section>
          <section className="rh-carte">
            <TitreSection>Mouvements</TitreSection>
            <Mouvements mouvements={c.mouvements} actions={actionsLigne} />
          </section>
          <section className="rh-carte">
            <TitreSection>Échéancier</TitreSection>
            <Echeanciers versions={c.echeanciers} />
          </section>
          <section className="rh-carte">
            <TitreSection>Actes de gestion</TitreSection>
            <ActesGestionCreance c={c} droits={droits} vivante={vivante} onActe={setFenetre} />
          </section>
        </>
      )}
      {fenetre?.type === 'mouvement' && <FenetreMouvement creance={c} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'decider' && <FenetreDeciderMouvement mouvement={fenetre.m} valider={fenetre.valider} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'contrepasser' && <FenetreContrepasser mouvement={fenetre.m} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'proposer-acte' && <FenetreProposerActe ligne={ligneActe(c)} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'decider-acte' && <FenetreDeciderActe ligne={ligneActe(c)} decisionId={fenetre.decisionId} onFermer={() => setFenetre(null)} onFait={fait} />}
    </PageFiche>
  );
}

// ─── Paiement ──────────────────────────────────────────────────────────────────────────────────
function FenetreExecuter({ paiement, organisationId, onFermer, onFait }) {
  const api = useSocial();
  const piece = usePieceDepot(organisationId, 'PAIEMENT', paiement.id, paiement.mode === 'CHEQUE' ? 'COPIE_CHEQUE' : paiement.mode === 'VIREMENT' ? 'AVIS_VIREMENT' : 'RECU');
  return (
    <FenetreActe titre="Exécuter le paiement" Icone={Play} libelleBouton={`Exécuter ${francs(paiement.montant)}`} pret={piece.prete} onFermer={onFermer}
      aide="Le second signataire n'exécute pas. L'exécution attribue le numéro, passe l'écriture de démonstration et, pour un chèque, le marque émis."
      onConfirmer={async (cle) => { const p = await piece.deposer(); await api.executerPaiement(paiement.id, { piece_id: p.id }, cle); onFait('Paiement exécuté.'); }}>
      <ChampPiece piece={piece} types={PIECES_PAIEMENT} libelleChamp="Pièce justificative (reçu, avis de virement, copie de chèque…)" />
    </FenetreActe>
  );
}

function FenetreAnnulerPaiement({ paiement, onFermer, onFait }) {
  const api = useSocial();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre="Annuler le paiement" Icone={Undo2} libelleBouton="Annuler le paiement" danger pret={motifValide(motif)} onFermer={onFermer}
      aide="Possible avant l'exécution seulement."
      onConfirmer={async (cle) => { await api.annulerPaiement(paiement.id, motif.trim(), cle); onFait('Paiement annulé.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

function FenetreSignature({ paiement, second, onFermer, onFait }) {
  const api = useSocial();
  return (
    <FenetreActe titre={second ? 'Contresigner le paiement' : 'Signer le paiement'} Icone={second ? FileSignature : PenLine} libelleBouton={second ? 'Contresigner' : 'Signer'} onFermer={onFermer}
      aide={second
        ? 'Le second signataire n\'est ni le préparateur, ni le premier signataire, ni le bénéficiaire.'
        : 'Le décideur de l\'octroi ne signe pas son décaissement ; le bénéficiaire ne signe jamais.'}
      onConfirmer={async (cle) => { await (second ? api.secondSignerPaiement(paiement.id, cle) : api.signerPaiement(paiement.id, cle)); onFait(second ? 'Paiement contresigné.' : 'Paiement signé.'); }}>
      <p style={{ margin: '0 0 12px', fontSize: 14 }}><strong>{paiement.libelle}</strong> — {francs(paiement.montant)}, {libelle(MODES_PAIEMENT, paiement.mode).toLowerCase()}, valeur au {dateLongue(String(paiement.date_valeur).slice(0, 10))}.</p>
    </FenetreActe>
  );
}

const ETAPES_PAIEMENT = ['Préparé', 'Signé', 'Contresigné', 'Exécuté'];
function Frise({ p }) {
  const rang = p.statut === 'EXECUTE' ? 3 : p.statut === 'SIGNE' ? (p.second_signataire_id ? 2 : 1) : p.statut === 'PREPARE' ? 0 : -1;
  return (
    <ol aria-label="Étapes du paiement" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 0', listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
      {ETAPES_PAIEMENT.map((e, i) => {
        const courant = i === rang;
        const franchi = i < rang;
        return (
          <li key={e} aria-current={courant ? 'step' : undefined} style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13, color: courant ? 'var(--sg-primaire)' : franchi ? 'var(--sg-texte-2)' : 'var(--sg-texte-4)', fontWeight: courant ? 600 : 400 }}>
            {i > 0 && <span aria-hidden="true" style={{ width: 20, height: 1, background: 'var(--sg-bord-fort)', margin: '0 8px' }} />}
            {e}
          </li>
        );
      })}
    </ol>
  );
}

export function PaiementPage() {
  const { org, id } = useParams();
  const api = useSocial();
  const { espaces } = useEspaces();
  const fiche = useLectureSociale(() => api.paiement(id, org), [api, id, org]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const p = fiche.donnees;
  const droits = droitsDans(espaces, p?.organisation_id || org);
  const fait = (m) => { setFenetre(null); setInfo(m); fiche.recharger(); };
  const attendSecond = p && p.statut === 'SIGNE' && p.double_signature_requise && !p.second_signataire_id;
  const executable = p && p.statut === 'SIGNE' && !attendSecond;
  const actions = p ? (
    <>
      {p.statut === 'PREPARE' && droits.a('ds_paiement_signer') && <Bouton variante="primaire" Icone={PenLine} onClick={() => setFenetre('signer')}>Signer</Bouton>}
      {attendSecond && droits.a('ds_paiement_second_signer') && <Bouton variante="primaire" Icone={FileSignature} onClick={() => setFenetre('second')}>Contresigner</Bouton>}
      {executable && droits.a('ds_paiement_executer') && <Bouton variante="primaire" Icone={Play} onClick={() => setFenetre('executer')}>Exécuter</Bouton>}
      {['PREPARE', 'SIGNE'].includes(p.statut) && droits.a('ds_paiement_preparer') && <Bouton variante="danger" Icone={Undo2} onClick={() => setFenetre('annuler')}>Annuler</Bouton>}
    </>
  ) : null;
  return (
    <PageFiche section="tresorerie" libelleSection="Trésorerie" titre={p ? (p.numero ? `Paiement ${p.numero}` : 'Paiement préparé') : 'Paiement'} actions={actions}>
      <BandeauComptaDemo />
      {fiche.erreur && (fiche.erreur.statut === 403 || fiche.erreur.statut === 404
        ? <section className="rh-carte"><AccesRefuseDs erreur={fiche.erreur} /></section>
        : <ErreurServeur erreur={fiche.erreur} />)}
      {!p && !fiche.erreur && <Squelette hauteur={160} />}
      {p && (
        <>
          <SuiviDossierGestion type="DS_PAIEMENT" id={id} cle={fiche.donnees} />
          {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
          {p.statut === 'EXECUTE' && <BandeauInfo ton="info">Un paiement exécuté s'annule par la contrepassation de son écriture, proposée puis validée par une autre personne (onglet Comptabilité).</BandeauInfo>}
          <section className="rh-carte">
            <Frise p={p} />
            <Infos lignes={[
              ['Libellé', p.libelle], ['Montant', <Montant n={p.montant} fort />], ['Sens', p.sens === 'ENCAISSEMENT' ? 'Encaissement' : 'Décaissement'], ['Bénéficiaire', p.beneficiaire],
              ['Objet', libelle(OBJETS_PAIEMENT, p.objet_type)], ['Mode', libelle(MODES_PAIEMENT, p.mode)], ['Date de valeur', dateLongue(String(p.date_valeur || '').slice(0, 10))],
              ['Référence', p.reference], ['Chèque', p.cheque ? `${p.cheque.chequier} n° ${p.cheque.numero}` : null], ['État', <Etat table={ETATS_PAIEMENT} code={p.statut} />],
              ['Seconde signature', p.double_signature_requise ? (p.second_signataire_id ? 'Faite' : 'Requise') : 'Non requise'],
              ['Préparé par', p.prepare_par_nom], ['Signé par', p.signe_par_nom], ['Contresigné par', p.second_signataire_nom], ['Exécuté par', p.execute_par_nom],
              ['Exécuté le', dateLongue(p.execute_le)], ['Motif d\'annulation', p.motif_annulation], ['Données', p.demonstration ? 'Démonstration' : null],
            ]} />
            {p.objet_type === 'AIDE' && p.objet_id && <p style={{ margin: '12px 0 0', fontSize: 14 }}><Link className="rh-lien" to={`/dispositifs/${p.organisation_id || org}/aides/${p.objet_id}`}>Voir la demande d'aide</Link></p>}
            {p.creance_id && <p style={{ margin: '12px 0 0', fontSize: 14 }}><Link className="rh-lien" to={`/dispositifs/${p.organisation_id || org}/tresorerie/creances/${p.creance_id}`}>Voir la créance</Link></p>}
            {p.objet_type === 'PRET' && p.objet_id && <p style={{ margin: '12px 0 0', fontSize: 14 }}><Link className="rh-lien" to={`/dispositifs/${p.organisation_id || org}/prets/${p.objet_id}`}>Voir le prêt</Link></p>}
          </section>
          <section className="rh-carte">
            <TitreSection>Signatures et historique</TitreSection>
            <HistoriqueActes lignes={p.historique} etats={ETATS_PAIEMENT} />
          </section>
          {p.piece_id && (
            <section className="rh-carte">
              <TitreSection>Pièce</TitreSection>
              <ListePieces pieces={[{ id: p.piece_id, contexte: 'Exécution du paiement' }]} />
            </section>
          )}
        </>
      )}
      {fenetre === 'signer' && <FenetreSignature paiement={p} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre === 'second' && <FenetreSignature paiement={p} second onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre === 'executer' && <FenetreExecuter paiement={p} organisationId={p.organisation_id || org} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre === 'annuler' && <FenetreAnnulerPaiement paiement={p} onFermer={() => setFenetre(null)} onFait={fait} />}
    </PageFiche>
  );
}
